import React, { useState, useEffect, useRef } from 'react';
import { Paper, Typography, TextField, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Grid, Button, Box, CircularProgress } from '@mui/material';
import { api } from '../services/api';
import axios from 'axios';
import { formFieldList } from '../services/xlsx';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import {recordEvent} from "../utils/analytics-wrapper";
import ErrorDialog from './ErrorDialog';
import {
  ThumbDown as ThumbDownFilledIcon, ThumbDownOutlined as ThumbDownIcon,
  ThumbUp as ThumbUpFilledIcon,
  ThumbUpOutlined as ThumbUpIcon
} from "@mui/icons-material";

const ExpressionCodingTools = ({ fileData, logger, anonGroup, location }) => {
  const [expressionDescription, setExpressionDescription] = useState('');
  const [expressionType, setExpressionType] = useState('');
  const [loading, setLoading] = useState(false);
  const cancelSource = useRef(null);
  const [result, setResult] = useState(null);
  const [resultType, setResultType] = useState(null);
  const [queryID, setQueryID] = useState(null);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formFields, setFormFields] = useState('');
  const [liked, setLiked] = React.useState(false);
  const [disliked, setDisliked] = React.useState(false);

  useEffect(() => {
    if (fileData) {
      setFormFields(formFieldList(fileData));
    }
  }, [fileData]);

  const codeExpression = async () => {
    // require description to be entered first
    if (!expressionDescription) {
      setErrorMessage("Please enter a description for the expression to code.");
      setOpenErrorDialog(true);
      return;
    }

    // cancel any previous API call for this button
    if (cancelSource.current) {
      cancelSource.current.cancel('Operation canceled by the user.');
    }

    // show that we're working and allow cancellation
    setLoading(true);
    const CancelToken = axios.CancelToken;
    cancelSource.current = CancelToken.source();

    // record analytics event
    recordEvent(
      {
        name: 'codeExpression',
        attributes: {
          expressionType
        },
        metrics: {
          descriptionLength: expressionDescription.length,
          formFieldsCount: formFields.length
        },
      },
      logger,
    );

    try {
      const response = await api.codeExpression(anonGroup, expressionDescription, expressionType,
          formFields, location, cancelSource.current.token);
      if (response.data.error && response.data.error.trim() !== '') {
        setResultType('error');
        setResult(response.data.error);
      }
      else {
        setResultType('expression');
        setLiked(false);
        setDisliked(false);
        setResult(response.data.expression);
        setQueryID(response.data.query_id)
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error(error);
        setResultType('error');
        setResult(error.message);
      }
    }

    setLoading(false);
    cancelSource.current = null;
  };

  const handleCloseErrorDialog = () => {
    setOpenErrorDialog(false);
  };

  const cancelRequest = () => {
    if (cancelSource.current) {
      cancelSource.current.cancel('Operation canceled by the user.');
      setLoading(false);
      cancelSource.current = null;
    }
  };

  const like = () => {
    if (liked) {
      api.restartLikeExpression(queryID, result, anonGroup);
      setLiked(false);
      setDisliked(false);
    } else {
      api.likeExpression(queryID, result, anonGroup);
      setLiked(true);
      setDisliked(false);
      recordEvent(
        {
          name: 'likeExpression',
          metrics: { expressionLength: result.length },
        },
        logger,
      );
    }
  };

  const dislike = () => {
    if (disliked) {
      api.restartLikeExpression(queryID, result, anonGroup);
      setDisliked(false);
      setLiked(false);
    } else {
      api.dislikeExpression(queryID, result, anonGroup);
      setDisliked(true);
      setLiked(false);
      recordEvent(
        {
          name: 'dislikeExpression',
          metrics: { expressionLength: result.length },
        },
        logger,
      );
    }
  };

  const copyText = () => {
    recordEvent(
      {
        name: 'copyExpression',
        metrics: { expressionLength: result.length },
      },
      logger,
    );
    navigator.clipboard.writeText(result);
  };

  return (
    <Grid item xs={12}>
      <Box m={1}>
        <Paper elevation={3} style={{ padding: '1em' }}>
          <Typography variant="h6" gutterBottom>
            AI expression coding
          </Typography>
          <Box mb={2}>
            <Typography variant="body1">
              Use this tool to help you code a SurveyCTO expression from a natural-language description.
            </Typography>
          </Box>
          <Box mb={2}>
            <TextField
              name="expressionDescription"
              multiline
              rows={2}
              variant="outlined"
              fullWidth
              label="Expression description"
              placeholder="Describe the expression you want..."
              value={expressionDescription}
              onChange={(e) => setExpressionDescription(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <FormControl component="fieldset">
            <Box display="flex" flexWrap="wrap" alignItems="center">
              <Box pr={2}>
                <FormLabel component="legend">Expression type:</FormLabel>
              </Box>
              <RadioGroup row aria-label="expressionType" name="expressionType" value={expressionType} onChange={(e) => setExpressionType(e.target.value)}>
                <FormControlLabel value="" control={<Radio />} label="Auto-detect" />
                <FormControlLabel value="relevance" control={<Radio />} label="Relevance" />
                <FormControlLabel value="constraint" control={<Radio />} label="Constraint" />
                <FormControlLabel value="calculation" control={<Radio />} label="Calculation" />
              </RadioGroup>
            </Box>
          </FormControl>
          <Grid container justifyContent="right">
            {loading ? (
              <Button variant="contained" color="secondary" onClick={cancelRequest}>
                Cancel
              </Button>
            ) : (
              <Button variant="contained" color="primary" onClick={codeExpression}>
                Code expression
              </Button>
            )}
            {loading &&
              <Box ml={2}>
                <CircularProgress />
              </Box>
            }
          </Grid>

          {result && (
            <Paper elevation={3} style={{ padding: '0.25em 1em 1em 1em', marginTop: '1em' }}>
                <Box mt={2} style={{ display: 'flex', alignItems: 'flex-start' }}>
                  {resultType === 'error' ? (
                    <>
                      <ErrorOutlineIcon style={{ color: 'red' }} />
                      <Typography variant="body1" style={{ marginLeft: '10px' }}>
                        {result}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CheckCircleOutlineIcon style={{ color: 'green' }} />
                      <Typography variant="body1" style={{ fontFamily: 'monospace', marginLeft: '10px' }}>
                        {result}
                      </Typography>
                    </>
                  )}
                  <Tooltip title="Copy to clipboard">
                    <span>
                      <IconButton aria-label="copy" onClick={copyText}
                                  style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 10, paddingRight: 10 }}>
                        <FileCopyIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {!(resultType === 'error') && (
                    <>
                      <Tooltip title="Flag response as good or helpful">
                        <span>
                          <IconButton aria-label="like" onClick={like} disabled={loading}
                                      style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 10, paddingRight: 10 }}>
                            {liked ? (
                              <ThumbUpFilledIcon
                                color={loading ? 'rgba(0, 0, 0, 0.26)' : 'secondary'}
                              />
                            ) : (
                              <ThumbUpIcon color={loading ? 'rgba(0, 0, 0, 0.26)' : 'secondary'} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Flag response as bad or unhelpful">
                        <span>
                          <IconButton aria-label="dislike" onClick={dislike} disabled={loading}
                                      style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 10, paddingRight: 10 }}>
                            {disliked ? (
                              <ThumbDownFilledIcon
                                color={loading ? 'rgba(0, 0, 0, 0.26)' : 'secondary'}
                              />
                            ) : (
                              <ThumbDownIcon
                                color={loading ? 'rgba(0, 0, 0, 0.26)' : 'secondary'}
                              />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </Box>
            </Paper>
          )}
        </Paper>
      </Box>
      <ErrorDialog open={openErrorDialog} handleClose={handleCloseErrorDialog} message={errorMessage} />
    </Grid>
  );
};

export default ExpressionCodingTools;
